import React from 'react'
import { useNavigate } from 'react-router-dom'

const PrivacyPolicyPage: React.FC = () => {
    const navigate = useNavigate()

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Hero Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-12 sm:py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="heading-hero mb-4 sm:mb-6 text-gray-900 dark:text-white">Privacy Policy</h1>
                    <p className="text-sm sm:text-body-lg max-w-2xl mx-auto text-gray-700 dark:text-gray-300 leading-relaxed">
                        Your privacy is important to us. This privacy policy explains how SideGO collects,
                        uses, and protects your information when you use our platform.
                    </p>
                    <div className="text-xs sm:text-ui-sm mt-3 sm:mt-4">
                        <span className="text-gray-500 dark:text-gray-400">Last updated: January 2024</span>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-12 sm:py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                    <div className="prose prose-gray dark:prose-invert max-w-none">
                        <div className="space-y-8 sm:space-y-10">
                            <section>
                                <h2 className="heading-section mb-4 sm:mb-5 text-gray-900 dark:text-white">Information We Collect</h2>
                                <div className="text-xs sm:text-body space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                                    <p>
                                        We collect information you provide directly to us, information we obtain automatically
                                        when you use our services, and information from third parties.
                                    </p>
                                    <h3 className="heading-card mt-5 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white text-sm sm:text-base">Information You Provide</h3>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc">
                                        <li className="text-xs sm:text-body">Account registration information (name, email, password)</li>
                                        <li className="text-xs sm:text-body">Profile information (bio, skills, experience, location)</li>
                                        <li className="text-xs sm:text-body">Gig postings and applications</li>
                                        <li className="text-xs sm:text-body">Messages and communications</li>
                                        <li className="text-xs sm:text-body">Payment information (processed securely by third-party providers)</li>
                                    </ul>

                                    <h3 className="heading-card mt-5 sm:mt-6 mb-2 sm:mb-3 text-gray-900 dark:text-white text-sm sm:text-base">Information We Collect Automatically</h3>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc">
                                        <li className="text-xs sm:text-body">Device information and usage data</li>
                                        <li className="text-xs sm:text-body">Log information (IP address, browser type, pages visited)</li>
                                        <li className="text-xs sm:text-body">Cookies and similar tracking technologies</li>
                                        <li className="text-xs sm:text-body">Location information (with your permission)</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="heading-section mb-4 sm:mb-5 text-gray-900 dark:text-white">How We Use Your Information</h2>
                                <div className="text-xs sm:text-body space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                                    <p>We use the information we collect to:</p>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc">
                                        <li className="text-xs sm:text-body">Provide, maintain, and improve our services</li>
                                        <li className="text-xs sm:text-body">Process transactions and send related information</li>
                                        <li className="text-xs sm:text-body">Send you technical notices and support messages</li>
                                        <li className="text-xs sm:text-body">Communicate with you about products, services, and events</li>
                                        <li className="text-xs sm:text-body">Monitor and analyze trends and usage</li>
                                        <li className="text-xs sm:text-body">Detect, investigate, and prevent fraudulent activities</li>
                                        <li className="text-xs sm:text-body">Personalize and improve your experience</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="heading-section mb-4 sm:mb-5 text-gray-900 dark:text-white">Information Sharing</h2>
                                <div className="text-xs sm:text-body space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                                    <p>
                                        We may share your information in the following situations:
                                    </p>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc">
                                        <li className="text-xs sm:text-body"><strong>With your consent:</strong> We may share information when you give us consent</li>
                                        <li className="text-xs sm:text-body"><strong>Service providers:</strong> We may share information with third-party service providers</li>
                                        <li className="text-xs sm:text-body"><strong>Legal compliance:</strong> We may disclose information if required by law</li>
                                        <li className="text-xs sm:text-body"><strong>Business transfers:</strong> In connection with any merger, sale, or acquisition</li>
                                        <li className="text-xs sm:text-body"><strong>Public information:</strong> Information you choose to make public on your profile</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="heading-section mb-4 sm:mb-5 text-gray-900 dark:text-white">Data Security</h2>
                                <div className="text-xs sm:text-body space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                                    <p>
                                        We implement appropriate technical and organizational measures to protect your personal
                                        information against unauthorized access, alteration, disclosure, or destruction.
                                    </p>
                                    <p>
                                        However, no method of transmission over the internet or electronic storage is 100% secure.
                                        While we strive to use commercially acceptable means to protect your information, we cannot
                                        guarantee its absolute security.
                                    </p>
                                </div>
                            </section>

                            <section>
                                <h2 className="heading-section mb-4 sm:mb-5 text-gray-900 dark:text-white">Your Rights</h2>
                                <div className="text-xs sm:text-body space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                                    <p>
                                        Depending on your location, you may have the following rights regarding your personal information:
                                    </p>
                                    <ul className="space-y-1.5 sm:space-y-2 ml-4 sm:ml-6 list-disc">
                                        <li className="text-xs sm:text-body">Access to your personal information</li>
                                        <li className="text-xs sm:text-body">Correction of inaccurate information</li>
                                        <li className="text-xs sm:text-body">Deletion of your information</li>
                                        <li className="text-xs sm:text-body">Portability of your data</li>
                                        <li className="text-xs sm:text-body">Restriction of processing</li>
                                        <li className="text-xs sm:text-body">Objection to processing</li>
                                    </ul>
                                    <p>
                                        To exercise these rights, please contact us using the information provided below.
                                    </p>
                                </div>
                            </section>

                            <section>
                                <h2 className="heading-section mb-4 sm:mb-5 text-gray-900 dark:text-white">Contact Us</h2>
                                <div className="text-xs sm:text-body text-gray-700 dark:text-gray-300 leading-relaxed">
                                    <p className="mb-4 sm:mb-6">
                                        If you have any questions about this Privacy Policy, please reach out to us through our contact form.
                                    </p>
                                    <button
                                        onClick={() => navigate('/contact')}
                                        className="px-6 sm:px-8 py-2.5 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm sm:text-base rounded-lg transition-colors inline-block"
                                    >
                                        Contact Support
                                    </button>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default PrivacyPolicyPage